import { useCallback, useEffect, useMemo, useState } from "react";
import AppSidebar, { SB_W, SB_W_COLLAPSED } from "../../../AppSidebar";
import AccessControlSection from "../../rbac/components/AccessControlSection";
import McpSettingsSection from "../components/McpSettingsSection";
import { usePermissions } from "../../../context/PermissionsContext";
import { API } from "../../../cache";
import "../../../AppSidebar.css";
import "../styles/SettingsPage.css";

// ── Icons ─────────────────────────────────────────────────────────────────────

const IcoGeneral = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);
const IcoNotif = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);
const IcoIntegration = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 3 21 3 21 8"/>
    <line x1="4" y1="20" x2="21" y2="3"/>
    <polyline points="21 16 21 21 16 21"/>
    <line x1="15" y1="15" x2="21" y2="21"/>
  </svg>
);
const IcoEmail = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22 6 12 13 2 6"/>
  </svg>
);
const IcoCalendar = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const IcoShield = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const IcoSync = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="1 4 1 10 7 10"/>
    <polyline points="23 20 23 14 17 14"/>
    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15"/>
  </svg>
);
const IcoMcp = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1.5"/>
    <rect x="14" y="14" width="7" height="7" rx="1.5"/>
    <path d="M6.5 10v3a2 2 0 0 0 2 2H10 M17.5 14v-3a2 2 0 0 0-2-2H14"/>
  </svg>
);

// ── Nav config ─────────────────────────────────────────────────────────────────

const SECTIONS = [
  { key: "general",           label: "General",           Icon: IcoGeneral },
  { key: "holidays",          label: "Holidays",          Icon: IcoCalendar },
  { key: "access_control",    label: "Access Control",    Icon: IcoShield },
  { key: "third_party_data",  label: "Third Party Data",  Icon: IcoSync },
  { key: "notifications",     label: "Notifications",     Icon: IcoNotif },
  { key: "integrations",      label: "Integrations",      Icon: IcoIntegration },
  { key: "email",             label: "Email",             Icon: IcoEmail },
  { key: "mcp",               label: "MCP",               Icon: IcoMcp },
];

// ── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ message, type, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div className={`stp-toast stp-toast--${type || "success"}`} onClick={onDismiss}>
      {message}
    </div>
  );
}

// ── General Section ───────────────────────────────────────────────────────────

function GeneralSection({ showToast }) {
  const [cutoffSaved, setCutoffSaved] = useState(null);
  const [cutoffEdit,  setCutoffEdit]  = useState("");
  const [bufferSaved, setBufferSaved] = useState(null);
  const [bufferEdit,  setBufferEdit]  = useState("");
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API}/settings/general`, { credentials: "include" });
        if (!resp.ok) throw new Error("Failed to load settings");
        const data = await resp.json();
        if (!cancelled) {
          setCutoffSaved(data.timesheet_cutoff_working_days);
          setCutoffEdit(String(data.timesheet_cutoff_working_days));
          setBufferSaved(data.utilization_overbudget_buffer_pct);
          setBufferEdit(String(data.utilization_overbudget_buffer_pct));
        }
      } catch (err) {
        if (!cancelled) showToast?.(err.message, "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showToast]);

  // Both fields live in one org_settings row, so every PUT sends both
  // current values regardless of which row's Save button was clicked —
  // otherwise saving one field would silently reset the other to whatever
  // stale value happened to be in the other input.
  const saveGeneralSettings = async (overrides, successMessage) => {
    setSaving(true);
    try {
      const body = {
        timesheet_cutoff_working_days: Number(cutoffEdit),
        utilization_overbudget_buffer_pct: Number(bufferEdit),
        ...overrides,
      };
      const resp = await fetch(`${API}/settings/general`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.detail || "Failed to save settings");
      setCutoffSaved(data.timesheet_cutoff_working_days);
      setCutoffEdit(String(data.timesheet_cutoff_working_days));
      setBufferSaved(data.utilization_overbudget_buffer_pct);
      setBufferEdit(String(data.utilization_overbudget_buffer_pct));
      showToast?.(successMessage);
    } catch (err) {
      showToast?.(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCutoff = () => {
    const n = Number(cutoffEdit);
    if (!Number.isInteger(n) || n < 1 || n > 30) {
      showToast?.("Enter a whole number between 1 and 30", "error");
      return;
    }
    saveGeneralSettings({ timesheet_cutoff_working_days: n }, "Timesheet entry cutoff saved");
  };

  const handleSaveBuffer = () => {
    const n = Number(bufferEdit);
    if (!Number.isInteger(n) || n < 0 || n > 100) {
      showToast?.("Enter a whole number between 0 and 100", "error");
      return;
    }
    saveGeneralSettings({ utilization_overbudget_buffer_pct: n }, "Utilization over-budget buffer saved");
  };

  return (
    <>
      <p className="stp-section-title">Workspace</p>
      <h2 className="stp-section-heading">General Settings</h2>

      <div className="stp-card">
        <div className="stp-card-header">
          <div>
            <div className="stp-card-title">Workspace</div>
            <div className="stp-card-desc">Basic workspace configuration</div>
          </div>
        </div>
        <div className="stp-card-body">
          <div className="stp-row">
            <div>
              <div className="stp-row-label">Workspace Name</div>
              <div className="stp-row-hint">Displayed across the app and in email notifications</div>
            </div>
            <div className="stp-row-control">
              <div className="stp-empty-sub" style={{ fontStyle: "italic" }}>Coming soon</div>
            </div>
          </div>
          <div className="stp-row">
            <div>
              <div className="stp-row-label">Timezone</div>
              <div className="stp-row-hint">Used for due dates and scheduling</div>
            </div>
            <div className="stp-row-control">
              <div className="stp-empty-sub" style={{ fontStyle: "italic" }}>Coming soon</div>
            </div>
          </div>
          <div className="stp-row">
            <div>
              <div className="stp-row-label">Date Format</div>
              <div className="stp-row-hint">How dates are displayed across the app</div>
            </div>
            <div className="stp-row-control">
              <div className="stp-empty-sub" style={{ fontStyle: "italic" }}>Coming soon</div>
            </div>
          </div>
          <div className="stp-row">
            <div>
              <div className="stp-row-label">Timesheet Entry Cutoff</div>
              <div className="stp-row-hint">Working days after a date that a timesheet entry can still be created, edited, or submitted for it</div>
            </div>
            <div className="stp-row-control">
              {loading ? (
                <div className="stp-empty-sub" style={{ fontStyle: "italic" }}>Loading…</div>
              ) : (
                <div className="stp-cal-loc-row">
                  <input
                    type="number"
                    min={1}
                    max={30}
                    className="stp-cal-loc-input"
                    value={cutoffEdit}
                    onChange={e => setCutoffEdit(e.target.value)}
                  />
                  <button
                    className="stp-btn-ghost-sm"
                    disabled={saving || cutoffEdit === String(cutoffSaved)}
                    onClick={handleSaveCutoff}
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="stp-row">
            <div>
              <div className="stp-row-label">Utilization Over-Budget Buffer</div>
              <div className="stp-row-hint">Dashboard → Utilization: how far actual hours can run above or below the expected pace before an epic is flagged over-budget or behind schedule (%)</div>
            </div>
            <div className="stp-row-control">
              {loading ? (
                <div className="stp-empty-sub" style={{ fontStyle: "italic" }}>Loading…</div>
              ) : (
                <div className="stp-cal-loc-row">
                  <input
                    type="number"
                    min={0}
                    max={100}
                    className="stp-cal-loc-input"
                    value={bufferEdit}
                    onChange={e => setBufferEdit(e.target.value)}
                  />
                  <button
                    className="stp-btn-ghost-sm"
                    disabled={saving || bufferEdit === String(bufferSaved)}
                    onClick={handleSaveBuffer}
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Holidays Section ──────────────────────────────────────────────────────────

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtSynced(iso) {
  if (!iso) return "Never synced";
  const d = new Date(iso);
  return `Synced ${d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} at ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
}

function HolidaysSection({ showToast }) {
  const { perms } = usePermissions();
  const isAdmin = perms?.is_admin === true;
  const currentYear = new Date().getFullYear();

  const [calendars,     setCalendars]     = useState([]);
  const [holidays,      setHolidays]      = useState([]);
  const [years,         setYears]         = useState([]);
  const [selectedYear,  setSelectedYear]  = useState(currentYear);
  const [selectedCal,   setSelectedCal]   = useState("");
  const [loading,       setLoading]       = useState(true);
  const [locationEdits, setLocationEdits] = useState({});
  const [savingLoc,     setSavingLoc]     = useState({});

  const fetchCalendars = useCallback(async () => {
    const resp = await fetch(`${API}/holidays/calendars`, { credentials: "include" });
    if (!resp.ok) throw new Error("Failed to load calendars");
    const data = await resp.json();
    setCalendars(data);
    setLocationEdits(Object.fromEntries(data.map(c => [c.id, c.location || ""])));
    if (data.length > 0 && !selectedCal) setSelectedCal("");
  }, [selectedCal]);

  const fetchHolidays = useCallback(async (year, calId) => {
    const params = new URLSearchParams({ year: String(year) });
    if (calId) params.set("calendar_id", calId);
    const resp = await fetch(`${API}/holidays?${params}`, { credentials: "include" });
    if (!resp.ok) throw new Error("Failed to load holidays");
    return resp.json();
  }, []);

  const fetchYears = useCallback(async () => {
    const resp = await fetch(`${API}/holidays/years`, { credentials: "include" });
    if (!resp.ok) return;
    const data = await resp.json();
    setYears(data);
    if (data.length > 0 && !data.includes(selectedYear)) {
      setSelectedYear(data[0]);
    }
  }, [selectedYear]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await fetchCalendars();
      await fetchYears();
      const data = await fetchHolidays(selectedYear, selectedCal);
      setHolidays(data);
    } catch (err) {
      showToast?.(err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [fetchCalendars, fetchYears, fetchHolidays, selectedYear, selectedCal, showToast]);

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading) return;
    fetchHolidays(selectedYear, selectedCal)
      .then(setHolidays)
      .catch(err => showToast?.(err.message, "error"));
  }, [selectedYear, selectedCal]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveLocation = async (calId) => {
    setSavingLoc(s => ({ ...s, [calId]: true }));
    try {
      const resp = await fetch(`${API}/holidays/calendars/${calId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ location: locationEdits[calId] || null }),
      });
      if (!resp.ok) throw new Error("Failed to save location");
      showToast?.("Location saved");
      await fetchCalendars();
    } catch (err) {
      showToast?.(err.message, "error");
    } finally {
      setSavingLoc(s => ({ ...s, [calId]: false }));
    }
  };

  const displayedCalendar = useMemo(
    () => (selectedCal ? calendars.find(c => c.id === selectedCal) : calendars[0]),
    [calendars, selectedCal]
  );

  return (
    <>
      <p className="stp-section-title">Configuration</p>
      <h2 className="stp-section-heading">Holidays</h2>

      {/* Calendar cards (show location + last synced) */}
      {calendars.length > 0 && (
        <div className="stp-cal-cards">
          {calendars.map(cal => (
            <div key={cal.id} className={`stp-cal-card${displayedCalendar?.id === cal.id ? " stp-cal-card--active" : ""}`}
              onClick={() => setSelectedCal(cal.id === selectedCal ? "" : cal.id)}
            >
              <div className="stp-cal-card-name">{cal.name}</div>
              <div className="stp-cal-card-meta">{fmtSynced(cal.last_synced_at)}</div>
              {cal.location && <div className="stp-cal-card-loc">{cal.location}</div>}
              {isAdmin && (
                <div className="stp-cal-loc-row" onClick={e => e.stopPropagation()}>
                  <input
                    className="stp-cal-loc-input"
                    placeholder="Location (optional)"
                    value={locationEdits[cal.id] ?? ""}
                    onChange={e => setLocationEdits(s => ({ ...s, [cal.id]: e.target.value }))}
                  />
                  <button
                    className="stp-btn-ghost-sm"
                    disabled={savingLoc[cal.id] || locationEdits[cal.id] === (cal.location || "")}
                    onClick={() => handleSaveLocation(cal.id)}
                  >
                    {savingLoc[cal.id] ? "Saving…" : "Save"}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Year selector */}
      {years.length > 0 && (
        <div className="stp-year-strip">
          {years.map(y => (
            <button
              key={y}
              className={`stp-year-btn${selectedYear === y ? " stp-year-btn--active" : ""}`}
              onClick={() => setSelectedYear(y)}
            >
              {y}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="stp-hol-state">Loading…</div>
      ) : holidays.length === 0 ? (
        <div className="stp-empty" style={{ height: 240 }}>
          <svg className="stp-empty-icon" viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <div className="stp-empty-title">No holidays found</div>
          <div className="stp-empty-sub">
            {isAdmin ? 'Use “Sync from HR” to pull holidays from Keka.' : "No holidays are synced for this year yet."}
          </div>
        </div>
      ) : (
        <div className="stp-hol-table-wrap">
          <table className="stp-hol-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Date</th>
                <th>Day</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              {holidays.map(h => {
                const d = new Date(h.date + "T00:00:00");
                return (
                  <tr key={h.id}>
                    <td className="stp-hol-name">{h.name}</td>
                    <td className="stp-hol-date">{fmtDate(h.date)}</td>
                    <td className="stp-hol-day">{DAY_NAMES[d.getDay()]}</td>
                    <td>
                      {h.is_floater
                        ? <span className="stp-hol-badge stp-hol-badge--floater">Floater</span>
                        : <span className="stp-hol-badge stp-hol-badge--public">Public</span>
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── Third Party Data Section ──────────────────────────────────────────────────

function ThirdPartyDataSection({ showToast }) {
  const today      = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";

  const [syncingHr,  setSyncingHr]  = useState(false);
  const [hrResult,   setHrResult]   = useState(null);

  const [syncingHol, setSyncingHol] = useState(false);
  const [holResult,  setHolResult]  = useState(null);

  const [attFrom,    setAttFrom]    = useState(monthStart);
  const [attTo,      setAttTo]      = useState(today);
  const [syncingAtt, setSyncingAtt] = useState(false);
  const [attResult,  setAttResult]  = useState(null);

  const syncHr = async (mode = "daily") => {
    setSyncingHr(true);
    setHrResult(null);
    try {
      const usersOnly = mode === "usersOnly";
      const res = await fetch(
        `${API}/users/sync-hr${usersOnly ? "?sync_leave=false" : ""}`,
        { method: "POST", credentials: "include" }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "User sync failed");
      let msg = `Users synced — ${data.updated_users ?? 0} updated`;
      if (mode === "full") {
        const lvRes = await fetch(`${API}/leave-requests/sync?full=true`, { method: "POST", credentials: "include" });
        const lvData = await lvRes.json().catch(() => ({}));
        if (!lvRes.ok) throw new Error(lvData.detail || "Leave sync failed");
        msg += `, ${lvData.inserted ?? 0} leave records inserted`;
      } else if (mode === "daily" && data.leave_sync) {
        msg += `, ${data.leave_sync.inserted ?? 0} leave records synced`;
      }
      setHrResult({ ok: true, text: msg });
      showToast?.(msg, "success");
    } catch (err) {
      setHrResult({ ok: false, text: err.message });
      showToast?.(err.message, "error");
    } finally {
      setSyncingHr(false);
    }
  };

  const syncHolidays = async () => {
    setSyncingHol(true);
    setHolResult(null);
    try {
      const res = await fetch(`${API}/holidays/sync`, { method: "POST", credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Holiday sync failed");
      const msg = `Holidays synced — ${data.holidays_added ?? 0} added, ${data.holidays_updated ?? 0} updated`;
      setHolResult({ ok: true, text: msg });
      showToast?.(msg, "success");
    } catch (err) {
      setHolResult({ ok: false, text: err.message });
      showToast?.(err.message, "error");
    } finally {
      setSyncingHol(false);
    }
  };

  const syncAttendance = async () => {
    if (!attFrom || !attTo) { showToast?.("Select a date range first", "error"); return; }
    if (attFrom > attTo) { showToast?.("From date must be before To date", "error"); return; }
    setSyncingAtt(true);
    setAttResult(null);
    try {
      const res = await fetch(
        `${API}/attendance/sync?date_from=${attFrom}&date_to=${attTo}`,
        { method: "POST", credentials: "include" }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Attendance sync failed");
      const msg = `Attendance synced — ${data.inserted ?? 0} inserted, ${data.updated ?? 0} updated, ${data.soft_deleted ?? 0} removed`;
      setAttResult({ ok: true, text: msg });
      showToast?.(msg, "success");
    } catch (err) {
      setAttResult({ ok: false, text: err.message });
      showToast?.(err.message, "error");
    } finally {
      setSyncingAtt(false);
    }
  };

  return (
    <>
      <p className="stp-section-title">Admin</p>
      <h2 className="stp-section-heading">Third Party Data</h2>

      {/* ── Users & Leave Data ── */}
      <div className="stp-card">
        <div className="stp-card-header">
          <div>
            <div className="stp-card-title">Users &amp; Leave Data</div>
            <div className="stp-card-desc">Sync user profiles and leave requests from Keka HR</div>
          </div>
        </div>
        <div className="stp-card-body">
          <div className="stp-row">
            <div>
              <div className="stp-row-label">Daily Sync</div>
              <div className="stp-row-hint">Updates user profiles + last 30 days of leave data</div>
            </div>
            <div className="stp-row-control">
              <button className="stp-btn-primary" onClick={() => syncHr("daily")} disabled={syncingHr}>
                {syncingHr ? "Syncing…" : "Sync Now"}
              </button>
            </div>
          </div>
          <div className="stp-row">
            <div>
              <div className="stp-row-label">Full Leave Sync</div>
              <div className="stp-row-hint">Backfills complete leave history from Jan 1 of current year</div>
            </div>
            <div className="stp-row-control">
              <button className="stp-btn-primary" onClick={() => syncHr("full")} disabled={syncingHr}>
                {syncingHr ? "Syncing…" : "Full Sync"}
              </button>
            </div>
          </div>
          <div className="stp-row">
            <div>
              <div className="stp-row-label">Users Only</div>
              <div className="stp-row-hint">Updates user profiles only (user_id, mobile, location) — does not touch leave data</div>
            </div>
            <div className="stp-row-control">
              <button className="stp-btn-primary" onClick={() => syncHr("usersOnly")} disabled={syncingHr}>
                {syncingHr ? "Syncing…" : "Sync Users"}
              </button>
            </div>
          </div>
          {hrResult && (
            <div className={`stp-sync-result${hrResult.ok ? "" : " stp-sync-result--error"}`}>
              {hrResult.text}
            </div>
          )}
        </div>
      </div>

      {/* ── Holidays ── */}
      <div className="stp-card">
        <div className="stp-card-header">
          <div>
            <div className="stp-card-title">Holidays</div>
            <div className="stp-card-desc">Sync public holiday calendar from Keka HR</div>
          </div>
        </div>
        <div className="stp-card-body">
          <div className="stp-row">
            <div>
              <div className="stp-row-label">Sync Holidays</div>
              <div className="stp-row-hint">Fetches all calendars and holiday dates for the current year</div>
            </div>
            <div className="stp-row-control">
              <button className="stp-btn-primary" onClick={syncHolidays} disabled={syncingHol}>
                {syncingHol ? "Syncing…" : "Sync Now"}
              </button>
            </div>
          </div>
          {holResult && (
            <div className={`stp-sync-result${holResult.ok ? "" : " stp-sync-result--error"}`}>
              {holResult.text}
            </div>
          )}
        </div>
      </div>

      {/* ── Attendance ── */}
      <div className="stp-card">
        <div className="stp-card-header">
          <div>
            <div className="stp-card-title">Attendance</div>
            <div className="stp-card-desc">Sync employee office hours / attendance from Keka HR</div>
          </div>
        </div>
        <div className="stp-card-body">
          <div className="stp-row">
            <div>
              <div className="stp-row-label">Date Range</div>
              <div className="stp-row-hint">Select the period to sync — use a wide range for backfilling</div>
            </div>
            <div className="stp-row-control stp-att-range">
              <input
                type="date"
                className="stp-date-input"
                value={attFrom}
                max={attTo || today}
                onChange={e => setAttFrom(e.target.value)}
              />
              <span className="stp-att-range-sep">to</span>
              <input
                type="date"
                className="stp-date-input"
                value={attTo}
                min={attFrom}
                max={today}
                onChange={e => setAttTo(e.target.value)}
              />
              <button className="stp-btn-primary" onClick={syncAttendance} disabled={syncingAtt || !attFrom || !attTo}>
                {syncingAtt ? "Syncing…" : "Sync"}
              </button>
            </div>
          </div>
          {attResult && (
            <div className={`stp-sync-result${attResult.ok ? "" : " stp-sync-result--error"}`}>
              {attResult.text}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Placeholder Section ───────────────────────────────────────────────────────

function PlaceholderSection({ label }) {
  return (
    <>
      <p className="stp-section-title">Admin</p>
      <h2 className="stp-section-heading">{label}</h2>
      <div className="stp-empty">
        <svg className="stp-empty-icon" viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <div className="stp-empty-title">Coming soon</div>
        <div className="stp-empty-sub">This section is not yet configured.</div>
      </div>
    </>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────

export default function SettingsPage({ user }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sb-collapsed") === "true"
  );
  const [activeSection, setActiveSection] = useState("general");
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type, key: Date.now() });
  }, []);

  const sbW = sidebarCollapsed ? SB_W_COLLAPSED : SB_W;
  const section = SECTIONS.find(s => s.key === activeSection);

  return (
    <div className="stp-root" style={{ gridTemplateColumns: `${sbW} 1fr` }}>
      <AppSidebar
        user={user}
        collapsed={sidebarCollapsed}
        onCollapse={() => setSidebarCollapsed(c => {
          const next = !c;
          localStorage.setItem("sb-collapsed", String(next));
          return next;
        })}
      />

      <div className="stp-shell">
        <div className="page-head">
          <h1>Settings</h1>
        </div>

        <div className="stp-body">
          {/* Left nav */}
          <nav className="stp-nav">
            <div className="stp-nav-section-label">Configuration</div>
            {SECTIONS.map(({ key, label, Icon }) => (
              <button
                key={key}
                className={`stp-nav-item${activeSection === key ? " stp-nav-item--active" : ""}`}
                onClick={() => setActiveSection(key)}
              >
                <Icon />
                {label}
              </button>
            ))}
          </nav>

          {/* Content */}
          <div className="stp-content">
            {activeSection === "general"          && <GeneralSection showToast={showToast} />}
            {activeSection === "holidays"          && <HolidaysSection showToast={showToast} />}
            {activeSection === "access_control"    && <AccessControlSection showToast={showToast} />}
            {activeSection === "third_party_data"  && <ThirdPartyDataSection showToast={showToast} />}
            {activeSection === "mcp"               && <McpSettingsSection />}
            {activeSection !== "general" && activeSection !== "holidays" && activeSection !== "access_control" && activeSection !== "third_party_data" && activeSection !== "mcp" && (
              <PlaceholderSection label={section?.label ?? "Settings"} />
            )}
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          key={toast.key}
          message={toast.message}
          type={toast.type}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}
